import { InformationCircleIcon } from '@heroicons/react/24/outline';
import React, { useRef } from 'react';

import { OrganizationHeader } from '@/components/OrganizationHeader';
import { Tooltip } from '@/components/Tooltip';
import { ConfigurationStepProps } from '@/sections/customerManagement/components/modals/AddCustomerModal/AddCustomerModal.types';
import { useDateCalculation } from '@/sections/customerManagement/components/modals/AddCustomerModal/hooks/useDateCalculation';
import { usePilotDateTooltip } from '@/sections/customerManagement/components/modals/AddCustomerModal/hooks/usePilotDateTooltip';
import { PeriodOfPerformance } from '@/sections/customerManagement/components/PeriodOfPerformance';
import { getCustomerTypeDateRules } from '@/sections/customerManagement/utils';
import { ChariotModulesOptions } from '@/sections/settings/components';

/**
 * Step 3: Configuration - Period of performance, PlexTrac, and advanced features
 */
export const ConfigurationStep: React.FC<ConfigurationStepProps> = ({
  formData,
  setFormData,
  isSubmitting,
  enabledFlags,
  setEnabledFlags,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { handleStartDateChange, handleEndDateChange } = useDateCalculation({
    formData,
    setFormData,
  });

  // Filter function to only allow Mondays for PILOT customers
  const filterStartDate =
    formData.customer_type === 'PILOT'
      ? (date: Date) => date.getDay() === 1 // 1 = Monday
      : undefined;

  // Add tooltip for non-Monday dates when PILOT customer type is selected
  usePilotDateTooltip(containerRef, formData.customer_type === 'PILOT');

  return (
    <div className="flex h-[700px] flex-col" ref={containerRef}>
      <OrganizationHeader
        displayName={formData.settings_display_name}
        profilePicture={formData.profile_picture}
        subtitle="Configure additional settings"
        className="mb-8"
      />

      <div className="flex-1 overflow-y-auto">
        <div className="space-y-8">
          {/* Period of Performance Section */}
          <div>
            {formData.customer_type === 'PILOT' && (
              <div className="bg-chariot-brand-primary/10 border-chariot-brand-primary/30 mb-4 rounded-md border p-3">
                <p className="text-chariot-text-primary text-sm">
                  <span className="font-semibold">Pilot Program:</span>{' '}
                  {getCustomerTypeDateRules('PILOT')}
                </p>
              </div>
            )}
            <PeriodOfPerformance
              startDate={formData.subscription_start_date}
              endDate={formData.subscription_end_date}
              onStartDateChange={handleStartDateChange}
              onEndDateChange={handleEndDateChange}
              disabled={isSubmitting}
              required={true}
              showTitle={true}
              filterStartDate={filterStartDate}
            />
          </div>

          {/* Advanced Features Section */}
          <div>
            <div className="mb-3 flex items-center gap-2">
              <h4 className="text-chariot-text-primary text-base font-semibold">
                Advanced Features
              </h4>
              <Tooltip
                title="Premium capabilities available based on subscription and requirements."
                placement="top"
              >
                <InformationCircleIcon className="text-chariot-text-secondary size-5 cursor-help" />
              </Tooltip>
            </div>
            <ChariotModulesOptions
              isLoadingFlags={false}
              flags={enabledFlags}
              setFlags={setEnabledFlags}
              readOnly={false}
              disabledFlags={
                formData.customer_type === 'FREEMIUM'
                  ? ['disable_static_ip_addresses', 'disable_aiml_training']
                  : []
              }
              updateFlags={async ({
                flagsToAdd = [],
                flagsToDelete = [],
              }: {
                flagsToAdd?: string[];
                flagsToDelete?: string[];
                successMessage?: string;
                showToast?: boolean;
              }) => {
                // For FREEMIUM customers, prevent changes to specific flags
                if (formData.customer_type === 'FREEMIUM') {
                  const restrictedFlags = [
                    'disable_static_ip_addresses',
                    'disable_aiml_training',
                    'static',
                  ];
                  flagsToAdd = flagsToAdd.filter(
                    flag => !restrictedFlags.includes(flag)
                  );
                  flagsToDelete = flagsToDelete.filter(
                    flag => !restrictedFlags.includes(flag)
                  );
                }

                // Handle local state updates for new customer creation
                // These will be applied after successful account creation
                const newFlags = [...enabledFlags];

                // Remove flags that need to be deleted
                flagsToDelete.forEach(flag => {
                  const index = newFlags.indexOf(flag);
                  if (index > -1) {
                    newFlags.splice(index, 1);
                  }
                });

                // Add new flags
                flagsToAdd.forEach(flag => {
                  if (!newFlags.includes(flag)) {
                    newFlags.push(flag);
                  }
                });

                setEnabledFlags(newFlags);

                // Return a resolved promise to match the expected interface
                return Promise.resolve();
              }}
              prevFlags={[]}
            />
          </div>

          {/* PlexTrac Configuration Section - Show for engagement, pilot, and managed customers */}
          {(formData.customer_type === 'ENGAGEMENT' ||
            formData.customer_type === 'PILOT' ||
            formData.customer_type === 'MANAGED') && (
            <div>
              <h4 className="text-chariot-text-primary mb-3 text-base font-semibold">
                PlexTrac Configuration
              </h4>
              <div className="bg-chariot-background-secondary border-chariot-stroke rounded-md border p-3">
                <p className="text-chariot-text-secondary text-sm">
                  <span className="text-chariot-text-primary font-medium">
                    Note:
                  </span>{' '}
                  PlexTrac integration can be configured after customer creation
                  from the customer management drawer.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
